import React, { useEffect, useRef, useState } from "react";
import MagneticButton from "../ui/MagneticButton";
import AlignmentCue from "./AlignmentCue";

const HeroCTAs = () => {
  const lineRef = useRef(null);
  const [shimmer, setShimmer] = useState(0);

  // Drive a horizontal shimmer across the breakthrough line based on scroll.
  // The shimmer becomes more visible as the user begins to scroll — it reads
  // like the line "lights up" while the marks below snap into alignment.
  useEffect(() => {
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) {
      setShimmer(1);
      return undefined;
    }
    let raf = 0;
    const compute = () => {
      const el = lineRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const raw = 1 - Math.max(0, Math.min(1, (rect.top + rect.height * 0.5) / vh));
      setShimmer(Math.min(1, raw * 1.6));
    };
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(compute);
    };
    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <>
      <p
        data-testid="hero-cta-support"
        className="mt-10 max-w-xl mx-auto text-[15px] md:text-base leading-relaxed text-themed-muted animate-fade-in-up"
        style={{ animationDelay: "500ms" }}
      >
        Start by seeing where your energy, ideas, and opportunities are currently scattered. Then choose the next step that fits where you are.
      </p>

      <div
        className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 max-w-2xl mx-auto items-start animate-fade-in-up"
        style={{ animationDelay: "550ms" }}
      >
        <div className="flex flex-col items-center">
          <MagneticButton
            data-testid="hero-cta-snapshot"
            onClick={() => {
              window.location.href = "/frequency-snapshot";
            }}
            className="btn-gold"
          >
            Take the Free Clarity Snapshot
          </MagneticButton>
          <p
            data-testid="hero-snapshot-support-line"
            className="mt-3 text-[11.5px] leading-relaxed text-themed-muted max-w-[270px] text-center"
          >
            Free and reflective. No email required. You see your result first.
          </p>
        </div>
        <div className="flex flex-col items-center">
          <button
            type="button"
            data-testid="hero-cta-clarity-session"
            onClick={() => {
              window.dispatchEvent(new CustomEvent("am:open-orientation-reset-checkout"));
            }}
            className="btn-ghost-gold"
          >
            Book the Clarity Reset Session · $149
          </button>
          <p
            data-testid="hero-paid-support-line"
            className="mt-3 text-[11.5px] leading-relaxed text-themed-muted max-w-[270px] text-center"
          >
            Best if you already know something is circling and want guided help making sense of it.
          </p>
        </div>
      </div>

      <p
        ref={lineRef}
        data-testid="hero-breakthrough-line"
        data-text="From identity to clarity. From clarity to structure. From structure to execution. From execution to proof."
        className="mt-10 text-[11px] tracking-[0.22em] uppercase animate-fade-in-up scroll-shimmer"
        style={{
          animationDelay: "700ms",
          "--shimmer": shimmer,
        }}
      >
        From identity to clarity. From clarity to structure. From structure to execution. From execution to proof.
      </p>

      <p
        data-testid="hero-tertiary-framework"
        className="mt-6 text-[11px] tracking-[0.22em] uppercase text-gold/65 animate-fade-in-up"
        style={{ animationDelay: "780ms" }}
      >
        <a
          href="/framework"
          data-testid="hero-cta-framework"
          className="hover:text-gold transition-colors"
        >
          Explore the Framework
        </a>
      </p>

      <AlignmentCue />
    </>
  );
};

export default HeroCTAs;
